<script>
  import { getContext } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Button } from '@sveltestrap/sveltestrap';
  import { format as formatDate } from 'date-fns';
  import { unparse } from 'papaparse';

  import { Recipient } from '@client/components/recipient';
  import { RecipientFiltering } from '@client/components/recipient-filtering';

  import { appStore } from '@client/stores/app';

  import { downloadFile } from '@client/utils/download';

  import { ActivityItem } from './item';

  /**
   * @typedef {Object} ActivityNote
   * @property {string} [isp]
   * @property {string} [location]
   * @property {string} [country_code_iso3166alpha2]
   */

  /**
   * @typedef {Object} ActivityEntry
   * @property {string} [sender]
   * @property {string} [recipient]
   * @property {string} [utcts]
   * @property {string} [objecttype]
   * @property {string} [verb]
   * @property {string} [ip]
   * @property {ActivityNote} [note]
   * @property {string} [msgsubject]
   */

  /**
   * The full activity data set to be rendered and optionally grouped.
   * @type {ActivityEntry[]}
   */
  export let data = [];

  /**
   * Whether activity should be grouped by recipient.
   * @type {boolean}
   */
  export let grouped = false;

  /**
   * Whether previews (e.g. images, thumbnails) should be shown.
   * @type {boolean}
   */
  export let previews = true;

  /**
   * Whether sender info should be included in the display.
   * @type {boolean}
   */
  export let includeSender = false;

  /**
   * Whether to allow reporting of suspicious activity.
   * @type {boolean}
   */
  export let allowReporting = false;

  /**
   * Optional filter to isolate activity for a specific recipient.
   * @type {string|null}
   */
  export let recipientFilter = null;

  const { context } = getContext('config');

  const EXPORT_DATE_FORMAT = 'M-dd-yyyy-hh-mm-ss';
  const EXPORT_TYPE = 'text/csv;charset=utf-8';

  /**
   * Groups activity entries by UTC date string in the given field.
   * @param {ActivityEntry[]} data
   * @param {string} field
   * @returns {Object.<string, ActivityEntry[]>}
   */
  const groupByDates = (data, field) => {
    return data.reduce((acc, config) => {
      const date = formatDate(new Date(config[field]), 'M/d/yyyy');

      if (!acc[date]) {
        acc[date] = [];
      }

      acc[date].push(config);

      return acc;
    }, {});
  };

  /**
   * Filters out embedded/tracked events and groups the rest by date.
   * @param {ActivityEntry[]} data
   * @returns {Object.<string, ActivityEntry[]>}
   */
  const generateTimeline = (data) => {
    const events = data.filter(({ verb }) => verb !== 'embedded' && verb !== 'tracked');
    return groupByDates(events, 'utcts');
  };

  /**
   * Processes activity data into a grouped or ungrouped format depending on settings.
   * @param {ActivityEntry[]} activity
   * @returns {Object.<string, Object.<string, ActivityEntry[]>> | Object.<string, ActivityEntry[]>}
   */
  const generateAuditHistory = (activity) => {
    if (recipientFilter) {
      activity = activity.filter((entry) => entry.recipient === recipientFilter);
    }

    let recipients = {};

    if (grouped) {
      recipients = activity.reduce((acc, value) => {
        if (!acc[value.recipient]) {
          acc[value.recipient] = [];
        }

        acc[value.recipient].push(value);

        return acc;
      }, {});

      for (const recipient in recipients) {
        recipients[recipient] = recipients[recipient].sort((a, b) => new Date(b.utcts) - new Date(a.utcts));
        recipients[recipient] = generateTimeline(recipients[recipient]);
      }

      recipients = Object.keys(recipients)
        .sort()
        .reduce((acc, key) => {
          acc[key] = recipients[key];
          return acc;
        }, {});
    } else {
      recipients = {
        all: generateTimeline(activity.sort((a, b) => new Date(b.utcts) - new Date(a.utcts)))
      };
    }

    return recipients;
  };

  $: if (recipientFilter) {
    grouped = false;
  }

  /**
   * Exports activity data as a CSV file.
   */
  const exportHistory = () => {
    const fields = [
      $format('label.FROM'),
      $format('label.TO'),
      $format('label.DATE'),
      $format('label.TYPE'),
      $format('label.ACTION'),
      'IP',
      'ISP',
      $format('label.LOCATION'),
      'CC'
    ];

    const content = data.map((item) => [
      item.sender,
      item.recipient,
      item.utcts,
      item.objecttype,
      item.verb,
      item.ip,
      item?.note?.isp,
      item?.note?.location || '',
      item?.note?.country_code_iso3166alpha2 || ''
    ]);

    const text = unparse({ fields, data: content });
    const blob = new Blob([text], { type: EXPORT_TYPE });
    const today = formatDate(new Date(), EXPORT_DATE_FORMAT);
    const subject = data[0].msgsubject || 'activity';

    downloadFile(blob, `activity-${subject}-${today}.csv`);
  };

  $: appIsEmbedded = context?.source;
</script>

<div class="recipient-history-container">
  {#if data.length}
    <div class="actions">
      <Button size="sm" active={grouped} on:click={() => (grouped = !grouped)}
        >{$format('label.GROUP_BY_RECIPIENT')}</Button
      >
      {#if !$appStore.mobile && !appIsEmbedded}
        <Button kind="secondary" size="sm" on:click={exportHistory}>{$format('label.EXPORT_HISTORY')}</Button>
      {/if}
    </div>
  {/if}

  <RecipientFiltering bind:recipient={recipientFilter}>
    {@html $format('label.ACTIVITY_HISTORY_TIMELINE_RECIPIENT_FILTERING', { values: { filter: recipientFilter } })}
  </RecipientFiltering>

  {#if !data.length}
    <div class="empty">
      {$format('label.ACTIVITY_HISTORY_TIMELINE_EMPTY')}
    </div>
  {:else}
    {#key grouped || recipientFilter}
      {#each Object.entries(generateAuditHistory(data)) as [recipient, history]}
        <ActivityItem {recipient} {history} {grouped} {previews} {includeSender} {allowReporting} />
      {/each}
    {/key}
  {/if}
</div>

<style lang="scss" src="./activity-history.scss"></style>
